import {
  computeRepeatableSum,
  evaluateConditions,
  readUnitMode,
  type UnitMode,
} from '@bmo/form-core';

import type {
  ColumnUnitToggle,
  RepeatableColumn,
  RepeatableSumValidation,
  WorkflowField,
} from '@/api/workflows';

import type { FieldValue, RepeatableRowValue } from './FieldRenderer';

// The unit mode and the column total moved into @bmo/form-core when the PDF
// fill needed them: a column's total and its "$ or %" mode are now mappable
// into boxes, and the number the PDF prints must be the same number this module
// reports to the advisor. Re-exported so callers here are unchanged and there
// is exactly ONE implementation.
export {
  computeRepeatableSum,
  hasRepeatableColumnData,
  readUnitMode,
  resolveColumnLabel,
  unitModeKey,
  unitSymbol,
  type UnitMode,
} from '@bmo/form-core';

/**
 * The sum target that applies to a dual-unit column in its CURRENT mode, or
 * null when that mode carries no total rule.
 *
 * RIF constrains percentages only — dollar mode has no rule — so "no target"
 * is a legitimate configuration, not a misconfiguration. A dollar target gated by
 * `dollarTargetConditions` is also "no target" while its conditions don't hold.
 */
export function resolveUnitModeTarget(
  toggle: ColumnUnitToggle,
  mode: UnitMode,
  getValueById?: (id: string) => FieldValue,
): number | null {
  if (mode === 'dollar' && toggle.dollarTargetConditions?.length) {
    // A gated target with no way to read the form can't be judged — treat it as "no rule".
    if (!getValueById) return null;
    const values = new Proxy({} as Record<string, FieldValue>, {
      get: (_, id) => getValueById(String(id)),
    });
    if (!evaluateConditions(toggle.dollarTargetConditions, 'all', values)) return null;
  }
  const constant = mode === 'percent' ? toggle.percentTargetConstant : toggle.dollarTargetConstant;
  const fieldId = mode === 'percent' ? toggle.percentTargetFieldId : toggle.dollarTargetFieldId;
  if (typeof constant === 'number' && Number.isFinite(constant)) return constant;
  if (fieldId && getValueById) {
    const raw = getValueById(fieldId);
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
    if (typeof raw === 'string' && raw.trim().length > 0) {
      const parsed = Number.parseFloat(raw);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return null;
}

/**
/**
 * Resolve the target sum for a RepeatableRow's sumValidation. Prefers the
 * absolute `targetConstant`; falls back to the current value of the referenced
 * `targetFieldId`. Returns null when neither is set OR the referenced field
 * hasn't been filled — the caller treats "no target yet" as "no check yet"
 * so the runtime doesn't complain before the advisor has typed the anchor.
 */
export function resolveSumTarget(
  sv: RepeatableSumValidation,
  getValueById?: (id: string) => FieldValue,
): number | null {
  if (typeof sv.targetConstant === 'number' && Number.isFinite(sv.targetConstant)) {
    return sv.targetConstant;
  }
  if (sv.targetFieldId && getValueById) {
    const raw = getValueById(sv.targetFieldId);
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
    if (typeof raw === 'string' && raw.trim().length > 0) {
      const parsed = Number.parseFloat(raw);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return null;
}

/**
 * Convenience — returns true when the RepeatableRow's actual sum matches
 * its target (within a small epsilon to survive floating-point noise). Also
 * returns true when the rule doesn't apply (no sumValidation) OR the target
 * isn't yet resolvable — the "not yet checkable" cases must not block Next.
 */
export function isSumSatisfied(
  field: WorkflowField,
  rows: RepeatableRowValue[],
  getValueById?: (id: string) => FieldValue,
): boolean {
  const sv = field.sumValidation;
  if (sv) {
    const target = resolveSumTarget(sv, getValueById);
    const total = computeRepeatableSum(field, rows);
    if (target !== null && total !== null && Math.abs(total - target) >= 0.005) return false;
  }
  return unbalancedUnitColumns(field, rows, getValueById).length === 0;
}

/**
 * Dual-unit columns whose total does not match the target for their CURRENT
 * mode. Empty when every column balances, when no column is dual-unit, or when
 * the active mode carries no rule — RIF's dollar mode is unconstrained, and
 * "not checkable yet" must never block Next.
 *
 * Returned rather than reduced to a boolean so the caller can name the offending
 * column in the Next tooltip.
 */
export function unbalancedUnitColumns(
  field: WorkflowField,
  rows: RepeatableRowValue[],
  getValueById?: (id: string) => FieldValue,
): { column: RepeatableColumn; mode: UnitMode; total: number; target: number }[] {
  const out: { column: RepeatableColumn; mode: UnitMode; total: number; target: number }[] = [];
  for (const column of field.repeatableTemplate ?? []) {
    const toggle = column.unitToggle;
    if (!toggle) continue;
    const mode = readUnitMode(field, column, getValueById);
    const target = resolveUnitModeTarget(toggle, mode, getValueById);
    if (target === null) continue;
    const total = computeRepeatableSum(field, rows, column.id);
    if (total === null) continue;
    if (Math.abs(total - target) >= 0.005) out.push({ column, mode, total, target });
  }
  return out;
}
